import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import Layout from './components/Layout';
import { Suspense, lazy } from 'react';
import { CircularProgress, Box } from '@mui/material';
import PrivateRoute from './components/PrivateRoute';
import { createTheme } from './theme/theme';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Public pages — loaded eagerly (visitors land here first)
import { LoginPage } from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import { LandingPage } from './pages/LandingPage';
import FAQPage from './pages/FAQPage';
import { FeaturesPage } from './pages/FeaturesPage';
import { PricingPage, PricingPageApp } from './pages/PricingPage';
import { AboutPage } from './pages/AboutPage';
import { BillingSuccessPage, BillingCancelPage } from './pages/BillingStatusPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import NotFoundPage from './pages/NotFoundPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { ScrollToTop } from './components/ScrollToTop';

// Protected pages — lazy-loaded (only fetched when user navigates to them)
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DeckDetailPage = lazy(() => import('./pages/DeckDetailPage'));
const StudyPage = lazy(() => import('./pages/StudyPage'));
const CreateDeckPage = lazy(() => import('./pages/CreateDeckPage'));
const CreateCardPage = lazy(() => import('./pages/CreateCardPage'));
const StudyHistoryPage = lazy(() => import('./pages/StudyHistoryPage'));
const StudySetupPage = lazy(() => import('./pages/StudySetupPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotesPage = lazy(() => import('./pages/NotesPage').then(m => ({ default: m.NotesPage })));
const AIStudyPage = lazy(() => import('./pages/AIStudyPage'));
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const UpcomingTestsPage = lazy(() => import('./pages/UpcomingTestsPage'));

// Loading component for Suspense
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh'
    }}
  >
    <CircularProgress />
  </Box>
);

function AppRoutes() {
  const { settings } = useSettings();
  const theme = createTheme(settings);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <SubscriptionProvider>
            <Routes>
              {/* Public routes without Layout */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/billing/success" element={<BillingSuccessPage />} />
              <Route path="/billing/cancel" element={<BillingCancelPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="*" element={<NotFoundPage />} />

              {/* Protected routes with Layout */}
              <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/deck/:deckId" element={<DeckDetailPage />} />
                <Route path="/deck/new" element={<CreateDeckPage />} />
                <Route path="/deck/:deckId/card/new" element={<CreateCardPage />} />
                <Route path="/study" element={<StudyPage />} />
                <Route path="/study-setup" element={<StudySetupPage />} />
                <Route path="/history" element={<StudyHistoryPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/notes" element={<NotesPage />} />
                <Route path="/app/faq" element={<FAQPage />} />
                <Route path="/ai-study" element={<AIStudyPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/app/pricing" element={<PricingPageApp />} />
                <Route path="/upcoming-tests" element={<UpcomingTestsPage />} />
              </Route>
            </Routes>
          </SubscriptionProvider>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <AppRoutes />
      </SettingsProvider>
    </AuthProvider>
  );
}
